import React, { useState, useEffect } from 'react';
import { AdvancedVerification } from '../utils/verification';

const DeploymentInfo = () => {
  const [deploymentInfo, setDeploymentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gateways, setGateways] = useState([]);
  const [currentEnsName, setCurrentEnsName] = useState(null);

  // Function to detect current ENS name from URL
  const detectCurrentEnsName = () => {
    const currentUrl = window.location.href;
    
    // Pattern for PinMe deployments: something.pinit.eth.limo
    const pinmeMatch = currentUrl.match(/https:\/\/([a-z0-9]+)\.pinit\.eth\.limo/);
    if (pinmeMatch) {
      return `${pinmeMatch[1]}.pinit.eth`;
    }
    
    // Pattern for direct ENS: something.eth.limo
    const ensMatch = currentUrl.match(/https:\/\/([a-z0-9-]+\.eth)\.limo/);
    if (ensMatch) {
      return ensMatch[1];
    }
    
    // If we're on localhost or other domain, use a default for demo
    if (currentUrl.includes('localhost')) {
      return '3iyt6dhy.pinit.eth'; // Your current deployment for local testing
    }
    
    return null;
  };

  useEffect(() => {
    const loadDeploymentInfo = async () => {
      try {
        setLoading(true);
        
        // Detect current ENS name from URL
        const detectedEnsName = detectCurrentEnsName();
        setCurrentEnsName(detectedEnsName);
        
        if (!detectedEnsName) {
          setDeploymentInfo({
            error: 'Could not detect deployment ENS name',
            timestamp: new Date().toISOString()
          });
          setLoading(false);
          return;
        }

        const verifier = new AdvancedVerification(detectedEnsName);
        
        const [contentHash, gatewayList] = await Promise.all([
          verifier.getContentHash(),
          verifier.getAccessGateways()
        ]);

        const expectedCid = await verifier.getExpectedCid();
        
        setDeploymentInfo({
          ensName: detectedEnsName,
          contentHash,
          expectedCid,
          hasContentHash: !!contentHash,
          timestamp: new Date().toISOString()
        });
        
        setGateways(gatewayList);
      } catch (error) {
        console.error('Error loading deployment info:', error);
        setDeploymentInfo({
          error: error.message,
          timestamp: new Date().toISOString()
        });
      } finally {
        setLoading(false);
      }
    };

    loadDeploymentInfo();
  }, []);

  if (loading) {
    return (
      <div className="bg-dominant text-white rounded-xl p-6 shadow-lg">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex-1">
            <div className="h-6 bg-gray-600 rounded w-48 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-600 rounded w-64 mb-3 animate-pulse"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-600 rounded w-32 animate-pulse"></div>
              <div className="h-4 bg-gray-600 rounded w-40 animate-pulse"></div>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="h-10 bg-gray-600 rounded w-24 animate-pulse"></div>
            <div className="h-10 bg-gray-600 rounded w-32 animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-dominant text-white rounded-xl p-6 shadow-lg">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex-1">
          <h3 className="text-xl font-bold mb-2">Deployed via PinMe</h3>
          <p className="text-secondary mb-3">
            This frontend is pinned to IPFS and accessible via ENS for tamper-proof delivery
          </p>
          
          {currentEnsName ? (
            <div className="flex flex-wrap gap-4 text-sm">
              {/* ENS Name - Always available */}
              <div className="flex items-center gap-2">
                <i className="fas fa-globe text-secondary"></i>
                <span className="font-mono">ENS: {currentEnsName}</span>
              </div>

              {/* IPFS CID - Show if available */}
              {deploymentInfo?.expectedCid && (
                <div className="flex items-center gap-2">
                  <i className="fas fa-cube text-secondary"></i>
                  <span className="font-mono max-w-xs truncate" title={deploymentInfo.expectedCid}>
                    IPFS: {deploymentInfo.expectedCid.slice(0, 16)}...
                  </span>
                </div>
              )}

              {/* Content Hash Status */}
              {deploymentInfo && (
                <div className="flex items-center gap-2">
                  <i className={`fas fa-${deploymentInfo?.hasContentHash ? 'check-circle' : 'exclamation-triangle'} text-secondary`}></i>
                  <span>
                    Content Hash: {deploymentInfo?.hasContentHash ? 'Set' : 'Not Set'}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-secondary">Loading deployment information...</p>
          )}

          {/* Error Message */}
          {deploymentInfo?.error && (
            <div className="mt-2 text-xs text-orange-300">
              <i className="fas fa-exclamation-triangle mr-1"></i>
              {deploymentInfo.error}
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-3">
          {/* Primary Gateway Links */}
          {gateways.slice(0, 2).map((gateway, index) => (
            <a
              key={index}
              href={gateway.url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-secondary text-white px-4 py-2 rounded-lg hover:bg-accent transition-colors flex items-center gap-2"
            >
              <i className={`fas fa-${gateway.type === 'ens' ? 'external-link-alt' : 'cube'}`}></i>
              {gateway.name}
            </a>
          ))}
          
          {/* PinMe Dashboard Link */}
          <a
            href="https://pinme.eth.limo/"
            target="_blank"
            rel="noopener noreferrer"
            className="border border-secondary text-secondary px-4 py-2 rounded-lg hover:bg-secondary hover:text-white transition-colors flex items-center gap-2"
          >
            <i className="fas fa-rocket"></i>
            PinMe
          </a>
        </div>
      </div>
    </div>
  );
};

export default DeploymentInfo;